import { memo, useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { RoutePath } from 'consts/RoutePath';
import { convertFullToHalf } from 'common/CharacterConverter';
import { Validator } from 'validators/Validator';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { ApiError } from 'requests/RequestBase';
import { callCheckMailAddressDuplicateApi } from 'requests/RequestUser';
import { GroupType, callRegisterGroupApi, callSendInvitationApi } from 'requests/RequestGroup';
import { CircularProgress } from '@material-ui/core';
import { GroupAuth } from 'consts/GroupAuthConst';
import classes from './override.module.scss';

type Props = {};
type UserAddDto = {
	groupId: string;
};

export const UserAddTemplate = memo((_props: Props) => {
	const history = useHistory<UserAddDto>();
	const dto = history.location.state;
	const [mailaddress, setMailaddress] = useState('');
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setMailaddress(event.target.value);
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setMailaddress(convertFullToHalf(event.target.value));
	};

	const [formErrors, setFormErrors] = useState({ mailaddress: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';

	const [processLoading, setProcessLoading] = useState(false);
	const [isSend, setIsSend] = useState(false);
	const sendMail = async () => {
		setProcessLoading(true);
		setFormErrors((prev) => ({ ...prev, mailaddress: '' }));
		setActionError('');

		// メールアドレス確認
		const validator = new Validator(mailaddress);
		const mailaddressError = validator.require().charTypeEmail().end();
		if (!!mailaddressError) {
			setFormErrors({ mailaddress: mailaddressError });
			setProcessLoading(false);
			return;
		}

		// メールアドレス重複確認
		const checkMailResult = await callCheckMailAddressDuplicateApi({
			mailaddress,
		});
		if (checkMailResult.status !== HttpStatus.OK) {
			setActionError(ReactError.E9999);
			history.push(RoutePath.ERROR);
			return;
		}
		if (!checkMailResult.data.exist) {
			setFormErrors((prev) => ({ ...prev, mailaddress: 'このメールアドレスは登録されていません' }));
			setProcessLoading(false);
			return;
		}

		// グループ登録
		let groupId = dto?.groupId;
		if (!groupId) {
			const registerGroupResult = await callRegisterGroupApi(GroupType.FAMILY);
			if (registerGroupResult.status !== HttpStatus.OK) {
				history.push(RoutePath.ERROR);
				return;
			}
			groupId = registerGroupResult.data.group_id;
		}

		// グループ招待
		const sendInvitationResult = await callSendInvitationApi(groupId, { mailaddress });
		if (sendInvitationResult.status !== HttpStatus.OK) {
			const errorData = sendInvitationResult.data as ApiError;
			setActionError(errorData.message);
			setProcessLoading(false);
			return;
		}

		setIsSend(true);
		setProcessLoading(false);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'ユーザーの追加';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<main>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>
							<Link to={RoutePath.FAMILY_INFO}>家族情報</Link>
						</li>
						<li>ユーザーの追加</li>
					</ul>
				</div>
				<h1 className="page-head">ユーザーの追加</h1>
				{!isSend && (
					<>
						<div className="section-in" ref={scrollRef} style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>すでにGakkenIDを持っているユーザーを招待する</span>
							</div>
							<div className="section-txt-box">
								<div className="txt">
									<div>招待したい人のメールアドレスを入力し「メールを送信する」ボタンを押してください。</div>
									<div>ご登録のメールアドレス宛にGakkenID事務局からメールが届きます。</div>
									<div>メールに書かれているURLにアクセスして同意をしてもらうと、あなたの家族として登録されます。</div>
								</div>
							</div>

							{isFormError || isActionError ? (
								<div className="error-box" ref={errorRef}>
									{isActionError ? <p>{actionError}</p> : null}
									{isFormError ? (
										<>
											<p>※下記項目は全て必須となります。</p>
											<div className="error-box-in">
												{formErrors.mailaddress !== '' ? <p>【メールアドレス】{formErrors.mailaddress}</p> : null}
											</div>
										</>
									) : null}
								</div>
							) : null}

							<div className="form-box">
								<div className="form-head">
									<label htmlFor="mailadress">メールアドレス</label>
								</div>
								<div className="form-input">
									<input
										id="mailadress"
										type="text"
										name="mailaddress"
										value={mailaddress}
										onChange={handleChangeValue}
										onBlur={handleBlurConvertFullToHalfValue}
									/>
								</div>
								<p className="error">{formErrors.mailaddress}</p>
							</div>
							<div className="btn-box">
								<div className="btn gr">
									{processLoading ? <CircularProgress /> : <button onClick={sendMail}>メールを送信する</button>}
								</div>
							</div>
						</div>
						<div className="section-in" style={{ marginTop: 20 }}>
							<div className="section-head">
								<span>{`Gakken${GroupAuth.MEMBER.name}を登録する`}</span>
							</div>
							<div className="section-txt-box">
								<div className="txt">
									<div>メールアドレスを使わずにIDを発行することができます。</div>
									<div>※サービス利用時に決済ができないなど、一部機能に制限があります</div>
								</div>
							</div>
							<div className="btn-box">
								<div className="btn gr">
									<Link to={{ pathname: RoutePath.MEMBER_INPUT, state: dto }}>
										<span>登録</span>
									</Link>
								</div>
							</div>
							<div className={classes.memberInfoChgIdDiv}>
								{`「GakkenID」と「Gakken${GroupAuth.MEMBER.name}」の違いについては`}
								<a href="/faq.html#h" target="_blank">
									こちら
								</a>
							</div>
						</div>
					</>
				)}
				{isSend && (
					<div className="section-in" style={{ marginTop: 50 }}>
						<div className="section-head error-box">
							<p>まだユーザーの招待は完了していません</p>
						</div>
						<div className="section-txt-box">
							<div className="txt">
								<div>次のメールアドレスに招待メールを送信しました。</div>
								<div>24時間以内にメールに書かれているURLにアクセスして同意をしてもらってください。</div>
							</div>
						</div>
						<div className="info-box">
							<div className="info-head">メールアドレス</div>
							<div className="info-head">{mailaddress}</div>
						</div>
						<div className="btn-box">
							<div className="btn gr">
								<Link to={RoutePath.MY_PAGE}>
									<span>確認</span>
								</Link>
							</div>
						</div>
					</div>
				)}
			</section>
		</main>
	);
});
